import '../../../css/preproc/layout/_accordion.scss';
import { config } from '../../config/app-config';

export function initAccordion(options = {}) {
    const settings = {
        ...config.accordion,
        ...options
    };

    // Find all elements with data-section="accordion"
    const accordionSections = document.querySelectorAll('[data-section="accordion"]');

    if (!accordionSections.length) {
        return;
    }

    // Initialize each accordion section
    accordionSections.forEach((accordionSection, index) => {
        // Check if accordion has items (.accordion-case-item)
        const hasItems = accordionSection.querySelectorAll('.accordion-case-item').length > 0;

        // If accordion is empty and has accordion-cases class, create structure
        if (!hasItems && accordionSection.classList.contains('accordion-cases')) {
            createEmptyAccordionStructure(accordionSection);
        } else {
            // Initialize existing accordion elements
            initAccordionItems(accordionSection);
        }
    });
}

function initAccordionItems(accordionSection) {
    // Get all headers from content part for display in collapsed state
    const accordionItems = accordionSection.querySelectorAll('.accordion-case-item');

    accordionItems.forEach(item => {
        const headerText = item.querySelector('.header-text');
        const contentTitle = item.querySelector('.description-half h4');

        // If there's a title in content, copy it to header for collapsed state
        if (headerText && contentTitle) {
            // Subscribe to content title changes
            const observer = new MutationObserver((mutations) => {
                headerText.textContent = contentTitle.textContent;
            });

            // Start observing
            observer.observe(contentTitle, {
                childList: true,
                characterData: true,
                subtree: true
            });

            // Initialize header text
            headerText.textContent = contentTitle.textContent;
        }
    });
}

function createEmptyAccordionStructure(accordionSection) {
    // If page has initialized content-editor, connect new elements to it
    if (window.loadContentForNewElements) {
        const contentElements = accordionSection.querySelectorAll('[data-content]');
        window.loadContentForNewElements(contentElements);
    } else if (typeof loadContentForNewElements === 'function') {
        try {
            // Try to import required function
            const contentElements = accordionSection.querySelectorAll('[data-content]');
            loadContentForNewElements(contentElements);
        } catch (error) {
            console.warn('Could not load content for accordion elements:', error);
        }
    }
}

export function hasAccordion() {
    return document.querySelectorAll('[data-section="accordion"]').length > 0;
}

export function reinitializeAccordion() {
    initAccordion();
}

/**
 * Function to update accordion item order numbers
 */
export function updateAccordionNumbers(accordionSection) {
    if (!accordionSection) {
        // If section not passed, find all accordion sections
        const accordionSections = document.querySelectorAll('[data-section="accordion"]');
        accordionSections.forEach(section => updateAccordionNumbersInSection(section));
    } else {
        updateAccordionNumbersInSection(accordionSection);
    }
}

function updateAccordionNumbersInSection(section) {
    if (!section || !section.classList.contains('accordion-cases')) {
        return;
    }

    // Get all accordion items and sort by data-order attribute
    const items = Array.from(section.querySelectorAll('.accordion-case-item'));

    if (!items.length) return;

    // Sort items by data-order attribute
    items.sort((a, b) => {
        const orderA = parseInt(a.getAttribute('data-order') || '0', 10);
        const orderB = parseInt(b.getAttribute('data-order') || '0', 10);
        return orderA - orderB;
    });

    // Update numbers
    items.forEach((item, index) => {
        // Use index + 1 for display (starting from 1)
        const displayNumber = index + 1;

        // Update number in header
        const headerNumber = item.querySelector('.accordion-header .case-number');
        if (headerNumber) {
            headerNumber.textContent = displayNumber;
        }

        // Update number in expanded content
        const contentNumber = item.querySelector('.accordion-content .case-number');
        if (contentNumber) {
            contentNumber.textContent = displayNumber;
        }
    });
}

/**
 * Hook to update numbering after drag and drop blocks
 * Adds event listener to catch block order changes
 */
export function setupDragDropNumbersUpdate() {
    // Listen for block drag completion event
    document.addEventListener('blockOrderChanged', (event) => {
        const sectionElement = event.detail?.section;
        if (sectionElement) {
            updateAccordionNumbersInSection(sectionElement);
        } else {
            updateAccordionNumbers();
        }
    });

    // Also update numbers on any accordion structure changes
    const accordionSections = document.querySelectorAll('[data-section="accordion"]');

    accordionSections.forEach(section => {
        // Create observer for DOM changes
        const observer = new MutationObserver((mutations) => {
            // Update numbering on DOM changes
            updateAccordionNumbersInSection(section);
        });

        // Configure observation for adding/removing elements
        observer.observe(section, {
            childList: true, // Watch for adding/removing child elements
            subtree: false,  // Don't watch changes in nested elements
            attributes: true, // Watch for attribute changes
            attributeFilter: ['data-order'] // Only watch data-order attribute changes
        });
    });
}

// Function to initialize numbers in existing accordions
export function initAccordionNumbers() {
    updateAccordionNumbers();
    setupDragDropNumbersUpdate();
}